/**
 * Validation for the **goods DeCA**: one list of everything still missing.
 *
 * It used to be two, one per liable party, because the €401 penalty attaches
 * **per liable party** and "whose fault is it" seemed the question the screen
 * had to answer (design spec §3, architecture spec §17.1). Their testers said
 * otherwise on 2026-09-18: several people fill one form, and what they need is
 * what is still empty, not whose column it is. The apportionment is unchanged in
 * law and the penalty notice below the list still states it — the portal has
 * simply stopped asserting it as a layout.
 *
 * ⚠️ **The order is ours, and it has to be.** The server groups by party, so
 * `[...cargador, ...transportista]` would read as an arbitrary sequence — the
 * carrier's *Razón social* (form section 2) after the shipper's *Lugar de
 * origen* (section 3). The list is re-ordered into the order the fields appear
 * on screen, so the user can walk down it and walk down the form: section, then
 * consignment, then dictionary number. A column the dictionary does not know
 * keeps its arrival position at the end rather than being dropped.
 *
 * `severity: 'WARNING'` is separated out and never blocks. NIF and plate
 * checksums arrive as warnings because foreign operators and edge formats exist,
 * and refusing to produce a legally required document over a checksum would be
 * worse than the risk it mitigates.
 *
 * The same component serves the create form's sticky rail, the blocked-generation
 * state and the import review, which is what keeps those three from drifting
 * apart.
 *
 * ⚠️ **Goods only, and the type system cannot say so.** This takes `partyErrors`,
 * which a route sheet's 422 does not carry: FOM/1230/2013 has no
 * Cargador/Transportista split, so `PassengerValidationProblem` is flat and its
 * failures go to `PassengerValidationSummary` instead. Both draw through
 * `ValidationSummaryFrame`; what is *not* shared is this file's one remaining
 * assertion — that €401 attaches to each liable party.
 */

import { useTranslation } from 'react-i18next'
import type { FieldError, PartyErrors } from '@deca/api'

import { byFormPosition } from '../deca/fields.ts'
import { Icon } from './Icon.tsx'
import { ValidationSummaryFrame, type ValidationGroup } from './ValidationSummaryFrame.tsx'

export interface ValidationSummaryProps {
  partyErrors: PartyErrors[] | undefined
  warnings?: FieldError[]
  penaltyNotice?: { amountEur?: number; perParty?: boolean } | undefined
  /** Rendered when there is nothing wrong. */
  okMessage?: string
  compact?: boolean
}

/** Blocking = anything not explicitly a WARNING; the default severity is ERROR. */
function isBlocking(field: FieldError): boolean {
  return field.severity !== 'WARNING'
}

function inFormOrder(fields: FieldError[]): FieldError[] {
  return byFormPosition(
    fields,
    (field) => field.field,
    (field) => field.consignmentSeq,
  )
}

export function ValidationSummary({
  partyErrors,
  warnings,
  penaltyNotice,
  okMessage,
  compact,
}: ValidationSummaryProps) {
  const { t } = useTranslation()

  const received: FieldError[] = (partyErrors ?? []).flatMap((party) => party.fields)

  const groups: ValidationGroup[] = [
    {
      blocking: inFormOrder(received.filter(isBlocking)),
      warnings: received.filter((field) => !isBlocking(field)),
    },
  ]

  // Warnings that belong to no party — they carry no blocking failures, so this
  // group draws nothing but its warnings, and they stay last in the list.
  if (warnings?.length) groups.push({ blocking: [], warnings })

  const amount = penaltyNotice?.amountEur ?? 401

  return (
    <ValidationSummaryFrame
      groups={groups}
      blockingTitle={t('errors.validationTitle')}
      seqNote={(field) =>
        field.consignmentSeq == null
          ? null
          : t('create.consignment', { seq: field.consignmentSeq })
      }
      okMessage={okMessage}
      compact={compact}
      footer={
        <p className="small muted">
          <Icon name="alert" size={14} /> {t('party.penaltyExplain', { amount })}
        </p>
      }
    />
  )
}
